import { useState } from 'react';
import { useLazyCommand, useEvent } from '@soap/modules';
import { Mail } from 'lucide-react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { AlexMarkdownMessage } from './AlanMarkdownMessage';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { useToast } from '../ui/use-toast';

export type HelpChatMessageForEmail = {
  e305_UserMessage?: string;
  e305_AgentResponse?: string;
  e305_CreatedAt?: string;
};

type Props = {
  messages: HelpChatMessageForEmail[];
  /** When true, disables opening the dialog (e.g. while a chat message is sending). */
  chatBusy?: boolean;
  /** Appends Alex’s confirmation from the server (reference, optional note, 1–2 working days) when E306 arrives. */
  onSupportEmailQueued?: (AlexConfirmationMarkdown: string) => void;
};

function EmailTranscriptPreview({ messages }: { messages: HelpChatMessageForEmail[] }) {
  const hasAny = messages.some(
    (m) => m.e305_UserMessage?.trim() || m.e305_AgentResponse?.trim(),
  );
  if (!hasAny) {
    return <p className="text-xs text-gray-500">(No visible messages)</p>;
  }

  return (
    <div className="space-y-3">
      {messages.map((m, i) => {
        const user = m.e305_UserMessage?.trim();
        const agent = m.e305_AgentResponse?.trim();
        if (!user && !agent) return null;
        return (
          <div key={i} className="space-y-2 rounded-md border border-gray-200 bg-white p-2.5">
            {user && (
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">User</div>
                <div className="mt-1 whitespace-pre-wrap break-words rounded-md bg-[#1a5172] px-2.5 py-2 text-sm text-white">
                  {user}
                </div>
              </div>
            )}
            {agent && (
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Alex</div>
                <div className="mt-1 rounded-md bg-gray-100 px-2.5 py-2 text-sm text-gray-800">
                  <AlexMarkdownMessage content={agent} />
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function HelpChatEmailSupportControls({ messages, chatBusy, onSupportEmailQueued }: Props) {
  const [open, setOpen] = useState(false);
  const [consent, setConsent] = useState(false);
  const [additionalNote, setAdditionalNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();
  const [requestHelpChatEmail] = useLazyCommand('C307v1_RequestHelpChatEmailSupport');

  useEvent({
    eventName: 'E306v1_HelpChatSupportEmailQueued',
    onEventReceived: (event: { e306_AlexConfirmationMarkdown?: string }) => {
      setSubmitting(false);
      const body = event.e306_AlexConfirmationMarkdown?.trim() ?? '';
      if (body) {
        onSupportEmailQueued?.(body);
      }
      toast({ title: 'Request sent' });
      setOpen(false);
      setConsent(false);
      setAdditionalNote('');
    },
  });

  const hasMessages = messages.length > 0;

  const handleSend = () => {
    if (!consent || !hasMessages || submitting || chatBusy) return;
    setSubmitting(true);
    const trimmed = additionalNote.trim();
    requestHelpChatEmail(
      trimmed ? { C307_AdditionalNote: trimmed } : {}
    );
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="shrink-0 gap-1 border-[#1a5172] text-[#1a5172] hover:bg-[#1a5172]/5"
        disabled={chatBusy || !hasMessages}
        onClick={() => {
          setOpen(true);
          setConsent(false);
          setAdditionalNote('');
        }}
        aria-label="Email Learn Your Way support"
      >
        <Mail className="h-4 w-4 shrink-0" />
        <span className="sm:hidden">Email</span>
        <span className="hidden sm:inline">Email support</span>
      </Button>

      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) {
            setConsent(false);
            setSubmitting(false);
            setAdditionalNote('');
          }
        }}
      >
        <DialogContent className="flex max-h-[90dvh] flex-col gap-4 overflow-hidden p-6 sm:max-w-lg">
          <DialogHeader className="shrink-0 pr-6">
            <DialogTitle>Email Learn Your Way support</DialogTitle>
            <DialogDescription>
              We will send the support inbox a copy of our chat shown below.
            </DialogDescription>
          </DialogHeader>
          <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto overscroll-y-contain pr-1">
            <div>
              <p className="text-sm font-medium text-gray-700 mb-1">Preview</p>
              <div className="max-h-[min(32vh,240px)] overflow-y-auto rounded-md border border-gray-200 bg-gray-50 p-3 sm:max-h-[38vh]">
                <EmailTranscriptPreview messages={messages} />
              </div>
            </div>
            <div className="shrink-0">
              <Label htmlFor="help-email-additional-note" className="text-sm font-medium text-gray-700">
                Optional note for support
              </Label>
              <Textarea
                id="help-email-additional-note"
                value={additionalNote}
                onChange={(e) => setAdditionalNote(e.target.value)}
                disabled={submitting}
                maxLength={2000}
                placeholder="Anything else you want support to know (not already in the chat above)…"
                className="mt-1 min-h-[72px] max-h-32 resize-y border-gray-200 sm:max-h-40 focus-visible:border-[#1a5172] focus-visible:ring-0 focus-visible:ring-offset-0"
              />
              <p className="text-xs text-gray-500 mt-1">{additionalNote.length}/2000</p>
            </div>
          </div>
          <div className="flex shrink-0 items-start gap-2 border-t border-gray-100 pt-3">
            <Checkbox
              id="help-email-consent"
              checked={consent}
              onCheckedChange={(c) => setConsent(c)}
              disabled={submitting}
              className="mt-0.5"
            />
            <Label htmlFor="help-email-consent" className="text-sm font-normal leading-snug cursor-pointer">
              I understand this sends the visible chat, optional note, and my account email to Learn Your Way support.
            </Label>
          </div>
          <DialogFooter className="shrink-0 gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              type="button"
              className="bg-[#1a5172] hover:bg-[#164a65]"
              onClick={handleSend}
              disabled={!consent || submitting || chatBusy || !hasMessages}
            >
              {submitting ? 'Sending…' : 'Send email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
